import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SectionHeading from "@/components/feature/SectionHeading";
import MovieCard from "@/components/feature/MovieCard";
import EmptyState from "@/components/base/EmptyState";
import { usePageMeta } from "@/hooks/usePageMeta";
import { getMediaById, getRelatedMedia } from "@/utils/media";
import { typeLabels } from "@/utils/labels";

type PlayerState = "idle" | "loading" | "demo";

export default function PlayPage() {
  const { id = "" } = useParams();
  const media = getMediaById(id);

  const [episode, setEpisode] = useState(1);
  const [playerState, setPlayerState] = useState<PlayerState>("idle");
  const [toast, setToast] = useState("");

  usePageMeta({
    title: media
      ? `${media.title} 在线播放（演示） - 17C.COM`
      : "在线播放 - 17C.COM",
    description: media
      ? `在线观看《${media.title}》演示播放页，支持剧集切换演示。${media.synopsis.slice(0, 40)}... 播放内容仅为界面演示，暂无真实播放源。`
      : "该影视内容已下架或不存在。",
    keywords: media
      ? `${media.title}在线播放,${media.title}观看,影视播放演示,17C.COM`
      : "在线播放,17C.COM",
  });

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2400);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (playerState !== "loading") return;
    const timer = window.setTimeout(() => {
      setPlayerState("demo");
      setToast("暂无真实播放源，仅为演示");
    }, 1500);
    return () => window.clearTimeout(timer);
  }, [playerState]);

  if (!media) {
    return (
      <div className="mx-auto w-full max-w-7xl px-4 py-16 md:px-6">
        <EmptyState
          icon="ri-emotion-sad-line"
          title="该影视内容已下架或不存在"
          description="无法找到对应的播放内容，请返回浏览其他作品。"
          action={
            <Link
              to="/category"
              className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-700"
            >
              <i className="ri-play-list-line" aria-hidden="true" />
              浏览影视分类
            </Link>
          }
        />
      </div>
    );
  }

  const related = getRelatedMedia(media, 6);
  const episodes = Array.from({ length: Math.max(1, media.episodes) }, (_, i) => i + 1);

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: typeLabels[media.type], to: `/category/${media.type}` },
          { label: media.title, to: `/detail/${media.id}` },
          { label: "播放" },
        ]}
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <h1 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">
            {media.title}
            <span className="ml-2 align-middle text-sm font-medium text-foreground-400">
              {media.episodes > 1 ? `第 ${episode} 集` : "正片"}
            </span>
          </h1>

          {/* Player */}
          <div className="mt-4 overflow-hidden rounded-lg border border-foreground-800 bg-foreground-950">
            <div className="relative aspect-video w-full">
              <img
                src={media.cover}
                alt=""
                aria-hidden="true"
                className="h-full w-full object-cover object-top opacity-30"
              />
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-foreground-950/50 px-4 text-center">
                {playerState === "loading" ? (
                  <>
                    <span className="h-12 w-12 animate-spin rounded-full border-2 border-background-50/30 border-t-accent-400" />
                    <p className="mt-4 text-sm text-background-100">正在加载播放源...</p>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => setPlayerState("loading")}
                      aria-label="播放"
                      className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-500 text-background-50 transition-transform hover:scale-105 md:h-20 md:w-20"
                    >
                      <i className="ri-play-fill text-4xl md:text-5xl" aria-hidden="true" />
                    </button>
                    <p className="mt-4 text-sm font-medium text-background-100 md:text-base">
                      {playerState === "demo"
                        ? "播放器演示界面"
                        : "点击播放按钮体验播放器演示"}
                    </p>
                    <p className="mt-1 flex items-center gap-1.5 text-xs text-accent-300">
                      <i className="ri-information-line" aria-hidden="true" />
                      演示状态：本站暂无真实播放源，仅提供播放器界面演示
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Player control bar (demo) */}
            <div className="flex items-center gap-3 border-t border-foreground-800 bg-foreground-900 px-4 py-3">
              <button
                type="button"
                onClick={() => setPlayerState("loading")}
                aria-label="播放/暂停"
                className="flex h-8 w-8 items-center justify-center rounded-md text-background-100 transition-colors hover:bg-foreground-800"
              >
                <i className="ri-play-circle-line text-xl" aria-hidden="true" />
              </button>
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-foreground-800">
                <div className="h-full w-1/3 rounded-full bg-accent-500" />
              </div>
              <span className="text-xs text-background-300">00:12 / 45:00</span>
              <button
                type="button"
                aria-label="音量"
                className="flex h-8 w-8 items-center justify-center rounded-md text-background-100 transition-colors hover:bg-foreground-800"
              >
                <i className="ri-volume-up-line text-lg" aria-hidden="true" />
              </button>
              <button
                type="button"
                aria-label="全屏"
                className="flex h-8 w-8 items-center justify-center rounded-md text-background-100 transition-colors hover:bg-foreground-800"
              >
                <i className="ri-fullscreen-line text-lg" aria-hidden="true" />
              </button>
            </div>
          </div>

          {media.episodes > 1 && (
            <div className="mt-6">
              <h2 className="mb-3 flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-list-check-2 text-primary-500" aria-hidden="true" />
                选集（共 {media.episodes} 集）
              </h2>
              <div className="flex flex-wrap gap-2">
                {episodes.map((ep) => (
                  <button
                    key={ep}
                    type="button"
                    onClick={() => {
                      setEpisode(ep);
                      setPlayerState("idle");
                      setToast(`已切换到第 ${ep} 集（演示）`);
                    }}
                    className={`h-9 min-w-[52px] rounded-md px-3 text-sm font-medium transition-colors ${
                      ep === episode
                        ? "bg-primary-600 text-background-50"
                        : "bg-background-100 text-foreground-600 hover:bg-background-200"
                    }`}
                  >
                    第{ep}集
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">
              剧情简介
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              {media.synopsis}
            </p>
            <div className="mt-4 flex flex-wrap gap-2 text-xs text-foreground-500">
              <span className="rounded-full bg-background-100 px-3 py-1">
                {media.year} 年
              </span>
              <span className="rounded-full bg-background-100 px-3 py-1">
                {media.region}
              </span>
              {media.genres.map((g) => (
                <span key={g} className="rounded-full bg-background-100 px-3 py-1">
                  {g}
                </span>
              ))}
            </div>
          </div>
        </div>

        <aside className="lg:col-span-1">
          <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
            <h2 className="flex items-center gap-2 font-heading text-base font-bold text-accent-900">
              <i className="ri-alert-line" aria-hidden="true" />
              演示说明
            </h2>
            <ul className="mt-3 space-y-2 text-sm text-accent-900/80">
              <li className="flex gap-2">
                <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px]" aria-hidden="true" />
                本页播放器为前端界面演示，不含真实播放内容。
              </li>
              <li className="flex gap-2">
                <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px]" aria-hidden="true" />
                点击播放按钮会提示"暂无真实播放源，仅为演示"。
              </li>
              <li className="flex gap-2">
                <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px]" aria-hidden="true" />
                剧集切换仅用于展示交互样式。
              </li>
            </ul>
          </div>

          <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">
              作品信息
            </h2>
            <dl className="mt-3 space-y-2.5 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-foreground-400">导演</dt>
                <dd className="text-right text-foreground-700">{media.director}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-foreground-400">主演</dt>
                <dd className="text-right text-foreground-700">
                  {media.cast.join("、")}
                </dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-foreground-400">片长</dt>
                <dd className="text-right text-foreground-700">{media.duration}</dd>
              </div>
            </dl>
            <Link
              to={`/detail/${media.id}`}
              className="mt-4 flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
            >
              <i className="ri-information-line" aria-hidden="true" />
              查看作品详情
            </Link>
          </div>
        </aside>
      </div>

      <section className="mt-12">
        <SectionHeading icon="ri-movie-line" title="相关推荐" subtitle="继续观看更多精彩内容" />
        {related.length > 0 ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-6">
            {related.map((item) => (
              <MovieCard key={item.id} item={item} />
            ))}
          </div>
        ) : (
          <EmptyState title="暂无相关内容，敬请期待" />
        )}
      </section>

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-[70] -translate-x-1/2 animate-fade-up">
          <div className="flex items-center gap-2 rounded-md bg-foreground-950 px-4 py-3 text-sm font-medium text-background-50">
            <i className="ri-information-line text-accent-400" aria-hidden="true" />
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}